import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import ProductGrid from '../components/ProductGrid';
import CartSidebar from '../components/CartSidebar';
import useOrderData from '../components/hooks/userOrderData';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';

export default function OrderEntryPage() {
  const {  tableId, storeId} = useParams();
  const location = useLocation();
  const { waiterInfo, table } = location.state || {};
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';
  const tenantId = import.meta.env.REACT_APP_TENANT_ID;
  
  const { products, tableName, loading } = useOrderData(tableId, storeId);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(true);
  const [existingOrder, setExistingOrder] = useState(null);

  // Load existing order if table is occupied
  useEffect(() => {
    const fetchExistingOrder = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/orders`, {
          params: { table: tableId, status: 'in-progress' }
        });
  
        if (Array.isArray(response.data)) {
          // Process all orders and their products
          const combinedItems = response.data
            .flatMap(order => 
              (order.products || []).map(item => ({
                _id: item.productId?._id || item._id,
                name: item.productId?.name || item.name || 'Unknown Product',
                qty: item.quantity || 1,
                price: item.price || item.productId?.price || 0,
                note: item.note || ''
              }))
            )
            .reduce((acc, item) => {
              const existing = acc.find(i => i._id === item._id);
              existing ? existing.qty += item.qty : acc.push(item);
              return acc;
            }, []);
  
          setCart(combinedItems);
          if (response.data.length > 0) setExistingOrder(response.data[0]);
        }
      } catch (err) {
        console.error('Error loading orders:', err);
        toast.error('Failed to load existing orders');
      }
    };
  
    tableId && fetchExistingOrder();
  }, [tableId, apiUrl]);

  useEffect(() => {
    console.log('Current Cart:', cart);
  }, [cart]);

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

  const handleAddToCart = (product) => {
    setCart(prev => {
      const existingIndex = prev.findIndex(i => i._id === product._id);
      
      if (existingIndex >= 0) {
        const newCart = [...prev];
        newCart[existingIndex] = {
          ...newCart[existingIndex],
          qty: newCart[existingIndex].qty + 1
        };
        return newCart;
      }
      
      return [...prev, { 
        ...product, 
        qty: 1, 
        note: '',
        price: product.price // Ensure price is included
      }];
    });
    
    setSearchQuery('');
  };

  const handleQuantityChange = (productId, newQty) => {
    if (newQty < 1) return;
    
    setCart(prev => 
      prev.map(item => 
        item._id === productId ? { ...item, qty: parseInt(newQty) } : item
      )
    );
  };

  const handleRemoveItem = (productId) => {
    setCart(prev => prev.filter(item => item._id !== productId));
  };

  const handleNoteChange = (productId, note) => {
    setCart(prev =>
      prev.map(item =>
        item._id === productId ? { ...item, note } : item
      )
    );
  };


  const handleSubmitOrder = async (orderData, orderId) => {
    try {
      const orderData = {
        tenant: existingOrder?.tenant || tenantId,
        store: storeId,
        waiter: waiterInfo._id,
        tableNumber: table?.tableNumber,
        table: tableId,
        transactionType: 'dine-in',
        products: cart.map(item => ({
          productId: item._id,
          quantity: item.qty,
          price: item.price,
          note: item.note || ''
        })),
        totalAmount: subtotal,
        status: 'in-progress',
        customer: existingOrder?.customer
      };

      const url = existingOrder 
      ? `/api/orders/${existingOrder._id}`
      : '/api/orders';
    const method = existingOrder ? 'put' : 'post';

    await axios[method](url, orderData);
    toast.success('Order saved successfully!');

  
    } catch (error) {
      throw error; // Let CartSidebar handle the error
    }
  };

  const handleSubmitOrderx = async () => {
    try {
      const orderData = {
        tenant: existingOrder?.tenant || 'tenant-id', // Replace with actual tenant
        store: storeId,
        waiter: waiterInfo._id,
        tableNumber: table?.tableNumber,
        table: tableId,
        transactionType: 'dine-in',
        products: cart.map(item => ({
          productId: item._id,
          quantity: item.qty,
          price: item.price,
          note: item.note
        })),
        totalAmount: subtotal,
        status: existingOrder ? 'in-progress' : 'pending',
        customer: existingOrder?.customer
      };

      const url = existingOrder 
        ? `/api/orders/${existingOrder._id}`
        : '/api/orders';
      const method = existingOrder ? 'put' : 'post';

      await axios[method](url, orderData);
      toast.success('Order saved successfully!');
      
    } catch (err) {
      console.error('Error saving order:', err);
      toast.error('Failed to save order');
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">


      {/* Left: Product Section */}
      <div className="flex-1 p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold">
              Table: {table.tableNumber || 'N/A'}</h2>
            <h3 className="text-lg text-gray-600">
              Waiter: {waiterInfo.name || 'Not assigned'}</h3>
          </div>
          {existingOrder && (
            <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
              Editing Existing Order
            </span>
          )}
        </div>

        <input
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border rounded px-3 py-2 w-full mb-4"
        />

        {loading ? (
          <p>Loading menu...</p>
        ) : (
          <ProductGrid
            products={products || []} // Ensure it's always an array
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
            onAddToCart={handleAddToCart}
            onCategoryChange={setSelectedCategory}
          />
        )}
      </div>

      {/* Right: Cart */}
      <CartSidebar
          cart={cart}
          onQuantityChange={(productId, newQty) => {
            setCart(prev => 
              prev.map(item => 
                item._id === productId ? { ...item, qty: Math.max(1, newQty) } : item
              )
            );
          }}
          onRemoveItem={handleRemoveItem}
          onNoteChange={handleNoteChange}
          subtotal={subtotal}
          cartOpen={cartOpen}
          setCartOpen={setCartOpen}
          onSubmitOrder={handleSubmitOrder}
          existingOrder={existingOrder}
        />
    </div>
  );
}